import Head from "next/head";
import React, { useState } from "react";
import { AiOutlinePercentage } from "react-icons/ai";
import { HiOutlineBanknotes } from "react-icons/hi2";

import * as Slider from "@radix-ui/react-slider";
import CurrencyInput from "react-currency-input-field";

import { FiTarget } from "react-icons/fi";

import { getOGimage, getUrl } from "../../utils/getUrl";

function RentBudgetCalculator() {
  const [rentalPrice, setRentalPrice] = useState(0);
  const [netIncome, setNetIncome] = useState(70000);
  const [monthlyDebts, setMonthlyDebts] = useState(0);
  const [monthlySavings, setMonthlySavings] = useState(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [recommendedPercentage, setRecommendedPercentage] = useState(30);

  function rentBudgetCalculator() {
    let rentBasedOnNetIncome = netIncome * (recommendedPercentage / 100);
    let rentBudget =
      (netIncome - monthlySavings - monthlyDebts - monthlyExpenses) *
      (recommendedPercentage / 100);
    return [rentBasedOnNetIncome.toFixed(0), rentBudget.toFixed(0)];
  }
  let [rentUncut, rentDeducted] = rentBudgetCalculator();

  let totalExpenses = monthlySavings + monthlyDebts + monthlyExpenses;
  // let remainingAmt= parseInt(rentUncut.split(',').join(''))-totalExpenses;
  let remainingAmt =
    netIncome -
    (monthlyDebts + monthlyExpenses + monthlySavings + parseInt(rentUncut));
  // console.log(totalExpenses);

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta charSet="utf-8" />
        <title>Kenya Rent Budget Calculator</title>
        <link
          rel="canonical"
          href={`${getUrl()}/calculators/rent-budget-calculator`}
        />
        <meta
          name="description"
          content="Calculate your affordable monthly rent in Kenya with our Rent Budget Calculator. 
          Customize your income, expenses, and more and find the perfect rent budget"
        />
        <meta name="og:title" content="Kenya Rent Budget Calculator" />
        <meta
          name="og:description"
          content="Calculate your affordable monthly rent in Kenya with our Rent Budget Calculator. 
          Customize your income, expenses, and more and find the perfect rent budget"
        />
        <meta name="og:image" content={`${getOGimage()}`} />
        <meta
          property="og:url"
          content={`${getUrl()}/calculators/rent-budget-calculator`}
        />
      </Head>

      <main className=" flex flex-col space-y-8 px-5 py-8 lg:px-24 lg:py-16">
        <div className=" flex flex-col space-y-0">
          <h1 className=" text-2xl font-bold">Kenya Rent Budget Calculator</h1>
          <h2 className=" text-gray-700">
            Calculate your affordable monthly rent with our Rent Budget
            Calculator
          </h2>
        </div>

        <div className=" flex w-full flex-col rounded-md bg-blue-50 lg:h-96 lg:flex-row">
          <div className=" order-2 flex flex-col lg:order-1 lg:w-1/2">
            <div className=" flex flex-col space-y-1 px-5 pt-8 lg:px-8">
              <span className=" text-gray-900">
                Input the percentage of your income that you&apos;re willing to
                spend on rent
              </span>
              <Slider.Root
                className="  relative  flex h-5 w-full touch-none select-none items-center"
                min={10}
                max={40}
                step={1}
                defaultValue={[recommendedPercentage]}
                value={[recommendedPercentage]}
                onValueChange={(value) => {
                  setRecommendedPercentage(value);
                }}
              >
                <Slider.Track className=" flex w-full">
                  <Slider.Range className=" flex  h-3 w-full rounded-full bg-gray-800 " />
                </Slider.Track>
                <Slider.Thumb
                  className="  flex h-5 w-5 cursor-grab rounded-[10px]  bg-zote-primary  outline-none"
                  aria-label="Sales Price"
                />
              </Slider.Root>
              <div className=" flex flex-row">
                <div className=" h-5  w-[64.5%]">
                  <span>10%</span>
                </div>
                <div className=" flex  h-5 w-[35.5%] justify-between">
                  <span>30%</span>
                  <span>40%</span>
                </div>
              </div>
            </div>
            <div className=" order-2 grid w-full grid-cols-1 gap-y-4 rounded-l-md px-5 py-5 lg:order-1 lg:w-full lg:grid-cols-2 lg:gap-5 lg:gap-y-0 lg:px-8 lg:pb-8 lg:pt-5">
              <div className=" flex h-32 flex-col space-y-1">
                <div className=" flex flex-col">
                  <span className=" font-medium">Monthly net income</span>
                  <span className=" text-gray-600">Income after taxes</span>
                </div>

                <CurrencyInput
                  id="incomevalue"
                  name="incomevalue"
                  placeholder=""
                  className="rounded-md border border-gray-300 bg-white px-5 py-2"
                  allowDecimals={false}
                  allowNegativeValue={false}
                  min={0}
                  value={netIncome}
                  // prefix={netIncome > 0 ? "KES " : ""}
                  onValueChange={(value, name) => {
                    if (!value) {
                      setNetIncome(0);
                      // setMonthlyDebts(0);
                      return;
                    }
                    // if (value > 50000000) {
                    //   setNetIncome(50000000);
                    //   return;
                    // }
                    setNetIncome(parseInt(value));
                  }}
                />
              </div>
              <div className=" flex h-32 flex-col space-y-1">
                <div className=" flex flex-col">
                  <span className=" font-medium">Monthly savings</span>
                  <span className=" text-gray-600">Enter monthly savings</span>
                </div>

                <CurrencyInput
                  id="savingsvalue"
                  name="savingsvalue"
                  placeholder=""
                  className="rounded-md border border-gray-300 bg-white px-5 py-2"
                  allowDecimals={false}
                  allowNegativeValue={false}
                  min={0}
                  value={monthlySavings}
                  // prefix="KES "
                  onValueChange={(value, name) => {
                    if (!value) return setMonthlySavings(0);
                    setMonthlySavings(parseInt(value));
                  }}
                />
              </div>
              <div className=" flex h-32 flex-col space-y-1">
                <div className=" flex flex-col">
                  <span className=" font-medium">Monthly debts</span>
                  <span className=" text-gray-600">Enter debts</span>
                </div>

                <CurrencyInput
                  id="debtsvalue"
                  name="debtsvalue"
                  placeholder=""
                  className="rounded-md border border-gray-300 bg-white px-5 py-2"
                  allowDecimals={false}
                  allowNegativeValue={false}
                  min={0}
                  value={monthlyDebts}
                  // prefix="KES "
                  onValueChange={(value, name) => {
                    if (!value) return setMonthlyDebts(0);

                    setMonthlyDebts(parseInt(value));
                  }}
                />
              </div>
              <div className=" flex h-32 flex-col space-y-1">
                <div className=" flex flex-col">
                  <span className=" font-medium">Monthly expenses</span>
                  <span className=" text-gray-600">Enter expenses</span>
                </div>

                <CurrencyInput
                  id="expensevalue"
                  name="expensevalue"
                  placeholder=""
                  className="rounded-md border border-gray-300 bg-white px-5 py-2"
                  allowDecimals={false}
                  allowNegativeValue={false}
                  min={0}
                  value={monthlyExpenses}
                  // prefix="KES "
                  onValueChange={(value, name) => {
                    console.log(value);
                    if (!value) return setMonthlyExpenses(0);

                    setMonthlyExpenses(parseInt(value));
                  }}
                />
              </div>
            </div>
          </div>

          <div className=" relative order-1  flex w-full  flex-col rounded-r-md lg:order-2 lg:w-1/2 lg:flex-row">
            <div
              className={
                "sticky top-0 flex w-full items-center justify-center rounded-t-md py-8 transition-colors  duration-500  lg:relative lg:top-auto lg:w-1/2 lg:rounded-none lg:py-0 " +
                (recommendedPercentage > 30 || remainingAmt < 1
                  ? " bg-yellow-600 text-gray-200"
                  : "  bg-zote-primary text-gray-200")
              }
            >
              <div className=" flex flex-col items-center lg:space-y-1">
                <span className=" text-3xl font-semibold lg:text-4xl">
                  KES {Intl.NumberFormat().format(rentUncut)}
                </span>
                <span className=" text-base font-medium">
                  <span
                    className={
                      " " +
                      (recommendedPercentage > 30 || remainingAmt < 1
                        ? " hidden line-through"
                        : "")
                    }
                  >
                    affordable{" "}
                  </span>
                  rent per month
                </span>
              </div>
            </div>
            <div
              className={
                " flex w-full flex-row justify-between px-5 py-5  text-gray-900 transition-colors duration-500 lg:w-7/12 lg:flex-col  lg:justify-center lg:space-y-3 lg:rounded-r-md lg:px-10 lg:py-0" +
                (recommendedPercentage > 30 || remainingAmt < 1
                  ? " bg-yellow-100"
                  : "  bg-zote-background")
              }
            >
              <div className=" flex flex-col items-start space-y-1 lg:flex-row lg:items-center lg:space-x-3">
                <div
                  className={
                    " flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors duration-500 " +
                    (recommendedPercentage > 30 || remainingAmt < 1
                      ? " bg-yellow-600"
                      : " bg-zote-primary")
                  }
                >
                  <HiOutlineBanknotes className=" h-5 w-5 text-gray-100" />
                </div>
                <div className=" flex flex-col">
                  <span className=" font-semibold">Remaining Amount</span>
                  <span className=" text-sm">
                    KES {Intl.NumberFormat().format(remainingAmt)}
                  </span>
                </div>
              </div>
              <div className=" flex flex-col space-y-1 lg:flex-row lg:items-center lg:space-x-3">
                <div
                  className={
                    " flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors duration-500 " +
                    (recommendedPercentage > 30 || remainingAmt < 1
                      ? " bg-yellow-600"
                      : " bg-zote-primary")
                  }
                >
                  <AiOutlinePercentage className=" h-5 w-5 text-gray-100" />
                </div>
                <div className=" flex flex-col">
                  <span className=" font-semibold">Percentage of Income</span>
                  <span className=" text-sm">{recommendedPercentage}%</span>
                </div>
              </div>
              <div className=" flex flex-col space-y-1 lg:flex-row lg:items-center lg:space-x-3">
                <div
                  className={
                    " flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors duration-500 " +
                    (recommendedPercentage > 30 || remainingAmt < 1
                      ? " bg-yellow-600"
                      : " bg-zote-primary")
                  }
                >
                  <FiTarget className=" h-5 w-5 text-gray-100" />
                </div>
                <div className=" flex flex-col">
                  <span className=" font-semibold">Status</span>
                  <span className=" text-sm">
                    {recommendedPercentage > 30 || remainingAmt < 1
                      ? " Overbudget"
                      : " Affordable"}{" "}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default RentBudgetCalculator;
